import { Claim } from "@/api/receiver/getClaims";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { Calendar, FileText, Plus, User } from "lucide-react";
import {
  useCreateClaimDetail,
  useResolveClaim,
} from "../hooks/useClaimMutations";
import { useQuery } from "@tanstack/react-query";
import { FileInfo, getClaimFiles } from "@/api/receiver/getClaimFiles";
import { downloadClaimFile } from "@/api/receiver/downloadClaimFile";
import { useEffect, useState } from "react";
import { UploadFilePopup } from "./UploadFilePopup";
import { Progress } from "@/components/ui/progress";
import { removeUploadedClaimFiles } from "@/api/receiver/removeUploadedClaimFiles";

interface ExpandedClaimProps {
  claim: Claim;
  newDetail: { message: string };
  setNewDetail: (detail: { message: string }) => void;
}
export function ExpanedClaim({
  claim,
  newDetail,
  setNewDetail,
}: ExpandedClaimProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [uploadStatus, setUploadStatus] = useState<Record<string, number>>({});
  const [tempId, setTempId] = useState<string | undefined>(undefined);

  const { mutate: resolveClaim, isSuccess: resolveClaimSuccess } =
    useResolveClaim();
  const { mutate: addDetail, isSuccess: createClaimDetailSuccess } =
    useCreateClaimDetail();

  const { data: fileList, error } = useQuery({
    queryKey: ["claims", "claimDetailsFiles", claim.id],
    queryFn: () =>
      getClaimFiles({
        claimDetailIds: claim.detailsHistory.map((detail) => detail.id),
      }),
  });

  // Generate a temporary ID for the claim file upload
  function generateTempId(): string {
    return `temp-${Math.random().toString(36).replace(".", "")}`;
  }

  //TODO: NEED TO ADD TEMP FILE CLEAUP LOGIC
  useEffect(() => {
    const tempId = generateTempId();
    console.log("Generated tempId:", tempId);
    setTempId(tempId);
  }, [claim]);

  const load = claim.load;

  const handleAddDetail = async (claimId: string) => {
    if (!newDetail.message) return;

    const newDetailResponse = await addDetail({
      claimId,
      message: newDetail.message,
      tempId: tempId,
    });

    setFiles([]); // Clear files after adding detail
    setUploadStatus({}); // Reset upload status
    setNewDetail({ message: "" });
  };

  const handleCloseClaim = async (claimId: string) => {
    await resolveClaim(claimId);
  };

  const handleDownload = async (file: FileInfo) => {
    // Handle file download logic here
    console.log(`Downloading ${file.name}`);
    const url = await downloadClaimFile({
      claimDetailId: file.claimDetailId,
      fileName: file.name,
    });
    const win = window.open(url.signedUrl);
    if (win) {
      win.focus();
    }
  };

  return (
    <div>
      {/* Load Details */}
      {load && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold mb-3">Load Information</h3>
            {claim.state === "open" && (
              <Button
                onClick={() => handleCloseClaim(claim.id)}
                variant="outline"
                size="sm"
                className="mb-3"
              >
                Resolve Claim
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
            <div>
              <Label className="text-xs text-muted-foreground">
                Load Number
              </Label>
              <p className="font-medium">{load.name}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Carrier</Label>
              <p className="font-medium">
                {" "}
                {claim.load.carrierName || "Unassigned"}
              </p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">
                Pickup Date Window
              </Label>
              <p className="font-medium">
                {load.pickupStart && load.pickupEnd
                  ? format(load.pickupStart, "MMM dd, yyyy") +
                    " - " +
                    format(load.pickupEnd, "MMM dd, yyyy")
                  : "No pickup date set."}
              </p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">
                Delivery Date Window
              </Label>
              <p className="font-medium">
                {load.deliveryStart && load.deliveryEnd
                  ? format(load.deliveryStart, "MMM dd, yyyy") +
                    " - " +
                    format(load.deliveryEnd, "MMM dd, yyyy")
                  : "No delivery date set."}
              </p>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-muted-foreground">Route</Label>
              <p className="font-medium">
                {load.source} → {load.destination}
              </p>
            </div>
            {/* <div>
                            <Label className="text-xs text-muted-foreground">
                              Claim Assigned To
                            </Label>
                            <p className="font-medium">
                              {claim.submittedTo || "Claim Not Assigned"}
                            </p>
                          </div> */}
          </div>
        </div>
      )}

      {/* Claim Details */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Claim Activity</h3>
        <div className="space-y-3">
          {claim.detailsHistory.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4">
              No activity recorded yet.
            </p>
          ) : (
            claim.detailsHistory.map((detail) => {
              const files = fileList?.filter(
                (file) => file.claimDetailId === detail.id
              );
              return (
                <div
                  key={detail.id}
                  className="border-l-2 border-primary pl-4 py-2"
                >
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <User className="w-3 h-3" />
                    {detail.createdBy}
                    <Calendar className="w-3 h-3" />
                    {format(detail.createdAt, "MMM dd, yyyy 'at' HH:mm")}
                  </div>
                  <p className="text-md">{detail.message}</p>
                  {files && files.length > 0 && (
                    <div className="mt-2">
                      `
                      <Label className="text-xs text-muted-foreground">
                        Attached Files
                      </Label>
                      <ul className="flex flex-wrap pl-5 mt-1">
                        {files &&
                          files.map((file) => (
                            <li key={file.name} className="p-1">
                              <Button
                                onClick={() => handleDownload(file)}
                                variant={"outline"}
                              >
                                {file.name.split(".")[0].length > 30
                                  ? file.name.slice(0, 30) +
                                    "..." +
                                    file.name.slice(-3)
                                  : file.name}
                              </Button>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Add New Detail - Only show if claim is open */}
        {claim.state === "open" ? (
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor={`detail-${claim.id}`}>Add New Detail</Label>
            </div>
            <Textarea
              id={`detail-${claim.id}`}
              value={newDetail.message}
              onChange={(e) => setNewDetail({ message: e.target.value })}
              placeholder="Enter new information or status update..."
              className="min-h-[80px]"
            />
            <div className="flex flex-wrap items-center justify-start">
              {files.length > 0 &&
                files.map((file, index) => (
                  <div className="p-2 min-w-[350px]" key={index}>
                    <div className="flex items-center justify-between p-2 bg-muted rounded-md">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs truncate max-w-[300px]">
                          {file.name.split(".")[0].length > 10
                            ? file.name.slice(0, 10) +
                              "..." +
                              file.name.slice(-3)
                            : file.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      {/* <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button> */}
                      <div>
                        <span
                          className={`text-xs text-muted-foreground ${
                            uploadStatus[file.name] === -1 ? "text-red-500" : ""
                          }`}
                        >
                          {uploadStatus[file.name] === -1
                            ? "Error Uploading"
                            : uploadStatus[file.name] === 100
                            ? "Complete"
                            : "Uploading..."}
                        </span>
                      </div>
                    </div>
                    <Progress
                      value={uploadStatus ? uploadStatus[file.name] : 0}
                      className={` h-2 
                    `}
                    />
                  </div>
                ))}
            </div>
            <div className="flex items-left justify-between mt-2">
              <Button
                onClick={() => handleAddDetail(claim.id)}
                disabled={!newDetail.message}
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Note
              </Button>
              <UploadFilePopup
                files={files}
                setFiles={setFiles}
                uploadStatus={uploadStatus}
                setUploadStatus={setUploadStatus}
                tempId={tempId} // Pass the tempId to the upload context
              />
            </div>
          </div>
        ) : (
          <div className="border-t pt-4">
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-muted flex items-center justify-center">
                  <Calendar className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium">Claim Closed</p>
                <p className="text-xs">
                  No additional notes can be added to closed claims
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
